/**
 * Générateur — Il décompose un nombre entier en produit de facteurs premiers.
 * Attendu ID : 154 | Niveau : 4ème | Domaine : Divisibilité (desc 10, O19)
 *
 * ⚠️ Aucun exercice dans le doc prof pour O19 → CRÉATION BANQUE. Plan de cartes
 * validé 2026-07-14, tel qu'il est depuis la revue du 2026-09-23 :
 *
 *   Carte « Décomposer en produit de facteurs premiers » (id decompose), trois
 *   formats depuis le 2026-09-24 :
 *     - Saisie trous (decompose_texte_trou_saisie) : ligne KaTeX globale
 *       « 780 = 2 × 2 × 3 × {{5}} × 13 », UN seul facteur caché.
 *     - Saisie (decompose_texte) : « Décompose 780 en produit de facteurs
 *       premiers. », comparaison d'expression, tous les ordres admis.
 *     - QCM (reconnaitre_qcm_unique, clé gardée) : choix en écriture
 *       développée (KaTeX). Correcte = SEULE écriture en facteurs premiers de
 *       valeur n (théorème fondamental). Distracteurs nommés
 *       (REGLES_DISTRACTEURS_QCM).
 *
 *   Carte « retrouve » — Retrouve le nombre (saisie)
 *     Décomposition donnée (KaTeX) → produit unique.
 *
 *   Carte « exposant » — Combien de fois un facteur ? (saisie, suffixe « fois »)
 *     Nombre d'apparitions d'un premier p dans la décomposition, unique par
 *     construction.
 *
 *   Carte « diviseur » — Le plus petit diviseur premier (saisie), venue de 153
 *     n composé → plus petit facteur premier, unique par construction.
 *
 * Tout par construction (n bâti depuis ses facteurs, vivier énuméré au
 * chargement, aucune boucle de tirage) ; chaque tirage revérifié par les
 * divisions successives du cours (premiers.js), throw sinon.
 *
 * Revue 2026-09-23 (vague 153-163), attendu de fin de 4e : « Il décompose un
 * nombre entier en produit de facteurs premiers. » Exemple de réussite : « Il
 * décompose 780 en produit de facteurs premiers. » Cours du prof (19Divi-np) :
 * écriture DÉVELOPPÉE (300 = 2 × 2 × 3 × 5 × 5 ; 231 = 3 × 7 × 11), par
 * divisions successives en testant 2, puis 3, puis 5…, « L'ordre des facteurs
 * n'a pas d'importance ».
 *   - Écriture : plus aucun exposant, ni dans les choix du QCM ni dans
 *     l'énoncé de « retrouve » ; tout en KaTeX (tex, produits en \times), la
 *     ligne à trous de « decompose » en mode mathématique global. Les
 *     exposants (60 = 2² × 3 × 5) sont l'automatisme de 3e 1221 : c'est le
 *     cours, mode auto, À ARBITRER.
 *   - Tirage partagé recadré (vivier ÉNUMÉRÉ au chargement, 124 nombres) : 1 à
 *     3 premiers distincts parmi 2, 3, 5, 7 (exposants 1 à 3), plus au plus UN
 *     dernier premier parmi 11 et 13 (exposant 1) ; 3 à 5 facteurs ;
 *     12 ≤ n ≤ 1 000 ; au moins deux premiers distincts (jamais une puissance
 *     pure, 8, 27, 32, 81) ; un tirage sur deux avec un facteur répété (102
 *     nombres), l'autre sans (22 nombres). 780 = 2 × 2 × 3 × 5 × 13 peut
 *     sortir (avant : impossible, jamais quatre premiers distincts), plus
 *     aucun carré de 11 ou de 13 (18,8 % avant), n jamais au-delà de 1 000
 *     (19,7 % au-delà de 1 100 avant). Le repli mort (360) disparaît avec la
 *     boucle.
 *   - « decompose » : A1 corrigé. Dans 73,5 % des tirages, deux trous
 *     attendaient des facteurs différents, et l'élève qui les remplissait dans
 *     un autre ordre donnait une décomposition juste comptée fausse. La
 *     consigne impose désormais l'ordre, en phrase : « Les facteurs sont
 *     rangés du plus petit au plus grand. » Échelon nouveau
 *     `decomposition_complete` (un tirage sur trois, PART_DECOMPOSITION_COMPLETE) :
 *     tous les facteurs en trous, le geste de l'exemple de réussite ;
 *     contenu.difficulte posé, échelle exportée (ECHELONS_DIFFICULTE, FORMATS
 *     §0.8).
 *   - « reconnaitre » : choix en écriture développée. Familles : facteur non
 *     premier (deux premiers fusionnés, même valeur), facteur répété oublié
 *     (valeur n / p), facteur en trop (valeur n × p), « 1 × » dans le produit
 *     (même valeur ; erreur à confirmer par le prof : mode auto, À ARBITRER).
 *     Trois distracteurs par tirage. L'option réduite à un seul facteur (« 3 »
 *     pour 75, 74,9 % des tirages avant) n'existe plus. Heuristique « centre »
 *     (l'option qui partage le plus de facteurs avec les autres) : clé seule
 *     en tête 73,5 % avant, 0 % après (toujours à égalité avec au moins un
 *     distracteur). RÉSIDU mesuré (tmp-revue-154, 10 000 tirages) : chaque
 *     famille décidée ajoute ou retire exactement un facteur, donc la clé est
 *     TOUJOURS la seule option au nombre de facteurs « du milieu » (100 %,
 *     contre 0 % avant en termes écrits, quand les exposants masquaient le
 *     compte). Aucune combinaison de trois des quatre familles ne l'évite ; il
 *     faudrait une famille qui garde le nombre de facteurs. À ARBITRER.
 *   - « exposant » : suffixe « fois » à côté du champ (« 2 » suffit, « 2 fois »
 *     était refusé) ; le mot « exposant » n'apparaît pas côté élève. Tirée
 *     dans la moitié « avec facteur répété » du vivier, le nombre de fois
 *     demandé uniforme parmi ceux présents : avec le tirage partagé tel quel,
 *     la réponse « 1 » serait juste dans 75,5 % des tirages (38,2 % avant).
 *     Écart assumé par le correcteur, mode auto, À ARBITRER.
 *   - « diviseur » : venue de 153 (même clé, même libellé), c'est la première
 *     étape de la méthode du cours ; n composé de trois chiffres au plus,
 *     impair quatre fois sur cinq (réponses 3, 5, 7, 11, 13), pair une fois sur
 *     cinq (réponse 2 : 49,5 % des tirages avant). Déplacement : mode auto,
 *     À ARBITRER.
 *   - Rappel brouillon IMPÉRATIF sur tous les formats (RAPPEL_BROUILLON),
 *     l'attendu se fait à la main (divisions successives).
 *   - Phrases périmées retirées de cet en-tête : l'interdiction du KaTeX dans
 *     les trous (dépassée par la ligne KaTeX globale) et l'écriture des
 *     choix en exposants Unicode.
 *
 * Révision 2026-09-24 (demandes du prof à l'écran) :
 *   - La carte « Reconnaître une décomposition en facteurs premiers » est
 *     fusionnée dans « Décomposer » : même geste, deux formats. La clé de
 *     format reconnaitre_qcm_unique est gardée (generator_key persisté).
 *   - Commutativité : l'ordre imposé « du plus petit au plus grand » ne
 *     suffisait pas (une écriture juste restait refusée, et l'ordre n'est pas
 *     une règle du cours). La saisie trous n'a plus qu'UN trou, et la
 *     décomposition entière devient une saisie en comparaison d'expression :
 *     tous les ordres, développé ou en puissances, « n = » facultatif
 *     (ecrituresDecomposition). L'élève tape *, le champ affiche × ; la
 *     lettre x n'est jamais une multiplication. La ligne grise « Pour
 *     multiplier, tape *. » disparaît à l'impression.
 *   - L'échelon « decomposition_complete » (contenu.difficulte) et
 *     ECHELONS_DIFFICULTE sont retirés : l'échelon est devenu le format Saisie.
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';
import { estPremier, decomposer, plusPetitFacteurPremier } from './premiers';

const ATTENDU_ID = 154;

const L_DECOMPOSE = 'Décomposer en produit de facteurs premiers';
const L_RETROUVE = 'Retrouver un nombre à partir de sa décomposition';
const L_EXPOSANT = 'Compter un facteur premier dans une décomposition';
const L_DIVISEUR = 'Trouver le plus petit diviseur premier';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF sur tous les formats : la
// décomposition se fait à la main, par divisions successives. Forme maison
// (ligne grise en italique, 147 et 149) ; juste avant la question ; aucune
// méthode soufflée.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// Consigne de clavier de la saisie complète : même ligne grise que le rappel
// brouillon, donc retirée à l'impression comme lui (utils/rappelBrouillon.js) ;
// sur papier, l'élève écrit × à la main. À l'écran, le champ affiche × dès que
// l'élève tape * (TexteLibreEleve, comparaison d'expression). La lettre x reste
// la variable : elle n'est jamais un signe de multiplication (décision prof
// 2026-09-24).
const AIDE_ETOILE = '<p class="italic text-slate-400">Pour multiplier, tape *.</p>';

// Exposants écrits en chiffres supérieurs (touche longue du 3 sur téléphone…) ;
// le juge ramène ² à ^2 mais pas les autres, d'où les deux écritures.
const EXPOSANTS_SUP = { 2: '²', 3: '³', 4: '⁴', 5: '⁵', 6: '⁶', 7: '⁷', 8: '⁸', 9: '⁹' };

/** Toutes les suites distinctes des éléments d'une liste (au plus 5 : 120 suites). */
const suitesDistinctes = (liste) => {
    if (liste.length <= 1) return [liste];
    const vus = new Set();
    const out = [];
    liste.forEach((x, i) => {
        if (vus.has(x)) return;
        vus.add(x);
        suitesDistinctes([...liste.slice(0, i), ...liste.slice(i + 1)]).forEach((s) => out.push([x, ...s]));
    });
    return out;
};

/**
 * Les écritures justes de la décomposition complète de n (liste finie pour la
 * comparaison d'expression, 2026-09-24) : les facteurs dans TOUS les ordres
 * (cours : « L'ordre des facteurs n'a pas d'importance ») ; chaque premier
 * répété écrit développé (2 × 2 × 2) ou en puissance (2^3, 2³), la forme du
 * cours de 4e étant développée et la puissance, juste, ne devant pas être
 * refusée ; « n = » devant, ou pas. Jamais « 1 × » (1 n'est pas premier),
 * jamais la lettre x. Écrites déjà normalisées (* et sans espace) : le juge
 * normalise la saisie de la même façon (× · → *, espaces retirés).
 */
export const ecrituresDecomposition = (n, facteurs) => {
    const compte = new Map();
    facteurs.forEach((p) => compte.set(p, (compte.get(p) || 0) + 1));
    const repetes = [...compte.keys()].filter((p) => compte.get(p) > 1);
    const ecritures = new Set();
    for (let masque = 0; masque < (1 << repetes.length); masque++) {
        const groupe = (p) => repetes.includes(p) && ((masque >> repetes.indexOf(p)) & 1) === 1;
        const styles = [...compte.keys()].some((p) => groupe(p) && compte.get(p) > 2) ? ['^', 'sup'] : ['^'];
        styles.forEach((style) => {
            const blocs = [];
            compte.forEach((k, p) => {
                if (groupe(p)) blocs.push(style === '^' ? `${p}^${k}` : `${p}${EXPOSANTS_SUP[k]}`);
                else for (let i = 0; i < k; i++) blocs.push(String(p));
            });
            suitesDistinctes(blocs).forEach((s) => {
                ecritures.add(s.join('*'));
                ecritures.add(`${n}=${s.join('*')}`);
            });
        });
    }
    return [...ecritures];
};

// ─── Vivier du tirage partagé (énuméré une fois au chargement) ──────────────
// 1 à 3 premiers distincts parmi 2, 3, 5, 7 (exposants 1 à 3), plus au plus un
// dernier premier parmi 11 et 13 (exposant 1) ; 3 à 5 facteurs ; 12 ≤ n ≤ 1 000 ;
// au moins deux premiers distincts. Facteurs rangés du plus petit au plus grand.
const PETITS = [2, 3, 5, 7];
const VIVIER = (() => {
    const out = [];
    for (let e2 = 0; e2 <= 3; e2++) {
        for (let e3 = 0; e3 <= 3; e3++) {
            for (let e5 = 0; e5 <= 3; e5++) {
                for (let e7 = 0; e7 <= 3; e7++) {
                    const exps = [e2, e3, e5, e7];
                    const nbPetits = exps.filter((e) => e > 0).length;
                    if (nbPetits < 1 || nbPetits > 3) continue;
                    for (const grand of [null, 11, 13]) {
                        const facteurs = [];
                        PETITS.forEach((p, i) => { for (let k = 0; k < exps[i]; k++) facteurs.push(p); });
                        if (grand) facteurs.push(grand);
                        if (new Set(facteurs).size < 2) continue;
                        if (facteurs.length < 3 || facteurs.length > 5) continue;
                        const n = facteurs.reduce((a, b) => a * b, 1);
                        if (n < 12 || n > 1000) continue;
                        out.push({ n, facteurs });
                    }
                }
            }
        }
    }
    return out;
})();
const aUnRepete = (facteurs) => new Set(facteurs).size < facteurs.length;
const AVEC_REPETE = VIVIER.filter((x) => aUnRepete(x.facteurs));
const SANS_REPETE = VIVIER.filter((x) => !aUnRepete(x.facteurs));

// Second chemin : la décomposition relue par les divisions successives
// (premiers.decomposer) doit redonner exactement les facteurs construits.
const verifie = ({ n, facteurs }) => {
    const relu = decomposer(n).flatMap(({ p, exp }) => Array(exp).fill(p));
    if (relu.join(',') !== facteurs.join(',') || !facteurs.every(estPremier)) {
        throw new Error(`gen_154 : décomposition de ${n} incohérente`);
    }
    return { n, facteurs };
};

// Un tirage sur deux avec un facteur répété (décisions 2026-09-23).
const tirerNombre = () => verifie(randomChoice(randInt(0, 1) === 0 ? AVEC_REPETE : SANS_REPETE));

const produitTex = (facteurs) => facteurs.join(' \\times ');

// ═════════ Carte « decompose » — Complète le produit ═════════

// UN SEUL trou (2026-09-24) : avec deux trous ou plus, l'élève qui les
// remplissait dans un autre ordre donnait un produit juste compté faux (la
// multiplication est commutative, le correcteur compare trou par trou). Avec
// un trou, le facteur manquant est unique quel que soit l'ordre, et la
// consigne n'a plus à imposer « du plus petit au plus grand ». La
// décomposition entière est la saisie ci-dessous.
const genDecomposeTrous = () => {
    const { n, facteurs } = tirerNombre();
    const trou = randInt(0, facteurs.length - 1);
    // Ligne KaTeX globale : le produit en \times, le trou au niveau 0.
    const rhs = facteurs.map((p, i) => (i === trou ? `{{${p}}}` : String(p))).join(' \\times ');
    const payload = makeTexteTrou(`$${n} = ${rhs}$`,
        RAPPEL_BROUILLON
        + '<p><b>Complète la décomposition en produit de facteurs premiers.</b></p>',
        { ...opts(L_DECOMPOSE), mode: 'saisie' });
    payload._meta.params = { n, facteurs, trou };
    return payload;
};

// La décomposition ENTIÈRE, tapée (2026-09-24) : le geste de l'exemple de
// réussite (« Il décompose 780 en produit de facteurs premiers »), qui était
// avant l'échelon « tous les facteurs en trous ». Comparaison d'expression :
// tous les ordres, développé ou en puissances (ecrituresDecomposition).
const genDecomposeSaisie = () => {
    const { n, facteurs } = tirerNombre();
    const ecritures = ecrituresDecomposition(n, facteurs);
    // Second chemin : chaque écriture admise, relue, redonne n en premiers seuls.
    ecritures.forEach((e) => {
        const produit = e.replace(/^\d+=/, '').split('*').reduce((acc, bloc) => {
            const m = bloc.match(/^(\d+)(?:\^(\d+)|([²³⁴⁵⁶⁷⁸⁹]))?$/);
            if (!m) throw new Error(`gen_154 : écriture illisible ${e}`);
            const p = Number(m[1]);
            const k = m[2] ? Number(m[2]) : m[3] ? Number(Object.keys(EXPOSANTS_SUP).find((c) => EXPOSANTS_SUP[c] === m[3])) : 1;
            if (!estPremier(p)) throw new Error(`gen_154 : facteur non premier dans ${e}`);
            return acc * p ** k;
        }, 1);
        if (produit !== n) throw new Error(`gen_154 : ${e} ne vaut pas ${n}`);
    });
    const payload = makeSaisie(facteurs.join(' × '),
        RAPPEL_BROUILLON
        + `<p><b>Décompose ${n} en produit de facteurs premiers.</b></p>`
        + AIDE_ETOILE,
        { ...opts(L_DECOMPOSE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = { n, facteurs, ecritures: ecritures.length };
    return payload;
};

// ═════════ Carte « reconnaitre » — La bonne décomposition (QCM) ═════════

// Résidu mesuré le 2026-09-23 (tmp-revue-154, 10 000 tirages) : chaque
// distracteur a un facteur de plus ou de moins que la clé, qui est donc
// toujours la seule option au nombre de facteurs « du milieu ». Aucune des
// quatre familles décidées ne garde le nombre de facteurs ; le signaler au prof
// plutôt que d'inventer une famille.
export const REGLES_DISTRACTEURS_QCM = {
    reconnaitre_qcm_unique: {
        support: 'n du tirage partagé (3 à 5 facteurs premiers de 2 à 13, n ≤ 1 000) ; les quatre choix en écriture développée, facteurs rangés du plus petit au plus grand (le rang ne distingue rien)',
        familles: {
            facteur_non_premier: 'même valeur, deux facteurs premiers fusionnés en un nombre non premier inférieur à 100 (4, 6, 15, 39, 91…) : la division successive arrêtée sur un nombre qui n\'est pas premier',
            facteur_oublie: 'valeur n / p : une division par un premier RÉPÉTÉ oubliée (2 × 3 × 5 × 13 pour 780) ; seulement quand n a un facteur répété',
            facteur_en_trop: 'valeur n × p : un premier de la décomposition écrit une fois de trop',
            un_dans_le_produit: 'même valeur, « 1 × » en tête du produit : le 1 de la dernière ligne des divisions successives recopié comme un facteur (le cours : 1 n\'est pas premier) ; erreur à confirmer par le prof, mode auto, À ARBITRER',
        },
        supprimees: {
            exposant_en_trop: 'remplacée par facteur_en_trop : même erreur, écrite sans exposant (cours du prof)',
            premier_manquant: 'supprimée : quand n n\'avait que deux premiers distincts, elle ne laissait qu\'un facteur (« 3 » pour 75, 74,9 % des tirages), que personne ne coche',
            exposants_a_un: 'repli jamais atteint (code mort)',
        },
        garantie: 'trois distracteurs par tirage, sans boucle ni repli : facteur_non_premier toujours (deux plus petits facteurs premiers, produit ≤ 49, donc une paire fusionnable existe toujours) ; si n a un facteur répété, deux familles parmi facteur_oublie, facteur_en_trop et un_dans_le_produit, sinon facteur_en_trop et un_dans_le_produit. Deux à deux distincts et distincts de la clé : la clé est la seule écriture sans 1 dont tous les facteurs sont premiers et de valeur n ; facteur_non_premier contient un facteur non premier, un_dans_le_produit un 1, facteur_oublie et facteur_en_trop ont une autre valeur (l\'une plus petite, l\'autre plus grande) ; revérifié en valeur et en primalité, throw sinon',
    },
};

const fusionnerNonPremier = (facteurs) => {
    const paires = [];
    for (let i = 0; i < facteurs.length; i++) {
        for (let j = i + 1; j < facteurs.length; j++) {
            if (facteurs[i] * facteurs[j] < 100) paires.push([i, j]);
        }
    }
    const [i, j] = randomChoice(paires);
    const reste = facteurs.filter((_, k) => k !== i && k !== j);
    return [...reste, facteurs[i] * facteurs[j]].sort((a, b) => a - b);
};

const genReconnaitreQcm = () => {
    const { n, facteurs } = tirerNombre();
    const repetes = [...new Set(facteurs.filter((p, i) => facteurs.indexOf(p) !== i))];
    const oublier = (p) => { const i = facteurs.indexOf(p); return facteurs.filter((_, k) => k !== i); };
    const enTrop = (p) => [...facteurs, p].sort((a, b) => a - b);
    const candidats = {
        facteur_non_premier: () => fusionnerNonPremier(facteurs),
        facteur_oublie: () => oublier(randomChoice(repetes)),
        facteur_en_trop: () => enTrop(randomChoice(facteurs)),
        un_dans_le_produit: () => [1, ...facteurs],
    };
    const secondes = repetes.length
        ? shuffle(['facteur_oublie', 'facteur_en_trop', 'un_dans_le_produit']).slice(0, 2)
        : ['facteur_en_trop', 'un_dans_le_produit'];
    const familles = ['facteur_non_premier', ...secondes];
    const listes = familles.map((f) => candidats[f]());

    // Second chemin : la clé est la seule écriture juste (valeur n, tous premiers).
    const valeur = (l) => l.reduce((a, b) => a * b, 1);
    const juste = (l) => valeur(l) === n && l.every((x) => x > 1 && estPremier(x));
    if (!juste(facteurs) || listes.some(juste)) throw new Error(`gen_154 reconnaitre : unicité rompue pour ${n}`);

    const affiche = (l) => tex(produitTex(l));
    const correct = affiche(facteurs);
    const choix = [correct, ...listes.map(affiche)];
    if (new Set(choix).size !== 4) throw new Error(`gen_154 reconnaitre : choix en double pour ${n}`);
    const payload = makeQcm(choix, correct,
        RAPPEL_BROUILLON
        + `<p><b>Quelle est la décomposition de ${n} en produit de facteurs premiers ?</b></p>`,
        opts(L_DECOMPOSE));
    payload._meta.params = { n, facteurs, distracteurs: familles };
    return payload;
};

// ═════════ Carte « retrouve » — Retrouve le nombre (saisie) ═════════

const genRetrouveSaisie = () => {
    const { n, facteurs } = tirerNombre();
    const payload = makeSaisie(String(n),
        RAPPEL_BROUILLON
        + `<p><b>Quel nombre a pour décomposition en produit de facteurs premiers ${tex(produitTex(facteurs))} ?</b></p>`,
        opts(L_RETROUVE));
    payload._meta.params = { n, facteurs };
    return payload;
};

// ═════════ Carte « exposant » — Combien de fois un facteur ? (saisie) ═════════

// Écart au tirage partagé (voir l'en-tête) : un nombre AVEC facteur répété, et le
// nombre de fois demandé tiré uniformément parmi ceux présents (1, 2 ou 3), puis
// un premier qui apparaît ce nombre de fois.
const genExposantSaisie = () => {
    const { n, facteurs } = verifie(randomChoice(AVEC_REPETE));
    const compte = (p) => facteurs.filter((q) => q === p).length;
    const distincts = [...new Set(facteurs)];
    const fois = randomChoice([...new Set(distincts.map(compte))]);
    const p = randomChoice(distincts.filter((q) => compte(q) === fois));
    const payload = makeSaisie(String(fois),
        `<p>On décompose ${n} en produit de facteurs premiers.</p>`
        + RAPPEL_BROUILLON
        + `<p><b>Combien de fois le facteur ${p} apparaît-il dans cette décomposition ?</b></p>`,
        { ...opts(L_EXPOSANT), suffixe: 'fois' });
    payload._meta.params = { n, p, fois };
    return payload;
};

// ═════════ Carte « diviseur » — Plus petit diviseur premier (saisie) ═════════

// Venue de 153 le 2026-09-23. Pour chaque réponse impaire p, les multiplicateurs
// m tels que n = p × m soit un nombre de trois chiffres au plus dont p est le
// plus petit facteur premier : m ≥ p et aucun facteur premier de m sous p.
const REPONSES_IMPAIRES = [3, 5, 7, 11, 13];
const MULTIPLICATEURS = Object.fromEntries(REPONSES_IMPAIRES.map((p) => {
    const ms = [];
    for (let m = p; p * m <= 999; m++) if (plusPetitFacteurPremier(m) >= p) ms.push(m);
    return [p, ms];
}));

const genDiviseurSaisie = () => {
    // Pair une fois sur cinq (réponse 2), impair sinon (3, 5, 7, 11, 13, un
    // cinquième chacune des quatre cinquièmes restants).
    const p = randInt(1, 5) === 1 ? 2 : randomChoice(REPONSES_IMPAIRES);
    const n = p === 2 ? 2 * randInt(2, 499) : p * randomChoice(MULTIPLICATEURS[p]);
    // Second chemin : n composé, et aucun premier plus petit que p ne le divise.
    let premierDiviseur = null;
    for (let d = 2; d <= n && premierDiviseur === null; d++) if (n % d === 0) premierDiviseur = d;
    if (premierDiviseur !== p || estPremier(n) || n > 999) throw new Error(`gen_154 diviseur : ${n} incohérent`);
    const payload = makeSaisie(String(p),
        RAPPEL_BROUILLON
        + `<p><b>Quel est le plus petit nombre premier qui divise ${n} ?</b></p>`,
        opts(L_DIVISEUR));
    payload._meta.params = { n, reponse: p };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    decompose_texte_trou_saisie: genDecomposeTrous,
    decompose_texte: genDecomposeSaisie,
    reconnaitre_qcm_unique: genReconnaitreQcm,
    retrouve_texte: genRetrouveSaisie,
    exposant_texte: genExposantSaisie,
    diviseur_texte: genDiviseurSaisie,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM', texte: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    // Carte « reconnaitre » fusionnée dans « decompose » (demande du prof,
    // 2026-09-24) : même geste, deux formats. Les clés de format restent
    // celles d'avant (generator_key persisté par le back).
    { id: 'decompose', label: L_DECOMPOSE, formatsList: [...buildFormats('decompose'), ...buildFormats('reconnaitre')] },
    { id: 'retrouve', label: L_RETROUVE, formatsList: buildFormats('retrouve') },
    { id: 'exposant', label: L_EXPOSANT, formatsList: buildFormats('exposant') },
    { id: 'diviseur', label: L_DIVISEUR, formatsList: buildFormats('diviseur') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
